import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import ActivityRow from "../../components/pm/ActivityRow";

const msg = (e) => e?.response?.data?.detail?.message || "Could not load the schedule.";
const BTN = "min-h-[44px] px-4 rounded-sm text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-dark";

// B7: project schedule with critical path (CPM), % complete and baseline vs current. Mobile-first list.
export default function ProjectSchedule() {
  const { pid } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(() => api.get(`/pm/projects/${pid}/schedule`).then((r) => { setS(r.data); setErr(""); }).catch((e) => setErr(msg(e))), [pid]);
  useEffect(() => { load(); }, [load]);
  const act = (fn) => fn().then(load).catch((e) => setNote(msg(e)));
  const progress = (id, pct) => act(() => api.patch(`/pm/activities/${id}/progress`, { percent_complete: pct }));
  const baseline = () => act(() => api.post(`/pm/projects/${pid}/baselines`).then((r) => setNote(`Baseline v${r.data.version} saved.`)));
  return (
    <AppShell title="Schedule" subtitle="Critical path, float and progress from the project schedule">
      <div className="mx-auto w-full max-w-[1000px] px-4 md:px-6 space-y-4" data-testid="pm-schedule">
        {err && <p role="alert" className="text-sm text-red-700" data-testid="pm-schedule-error">{err}</p>}
        {note && <p role="status" className="text-sm" data-testid="pm-schedule-note">{note}</p>}
        {s && (
          <>
            <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="pm-schedule-summary">
              <span>{s.start} → {s.finish} ({s.duration_days} d)</span>
              <span data-testid="pm-schedule-pct">{s.percent_complete ?? "—"}% complete</span>
              <span>{s.critical_path.length} critical</span>
              <span>{s.baseline ? `Baseline v${s.baseline.version}` : "No baseline yet"}</span>
              <button type="button" onClick={baseline} className={`${BTN} bg-erf-dark text-white ml-auto`} data-testid="pm-save-baseline">Save baseline</button>
            </div>
            {!s.activities.length && <p className="text-sm" data-testid="pm-schedule-empty">No activities yet. Add them in the project schedule.</p>}
            <ul className="space-y-2">{s.activities.map((a) => <ActivityRow key={a.id} a={a} onProgress={progress} />)}</ul>
            <p className="text-xs text-erf-dark/70">{s.method}</p>
            <nav className="flex flex-wrap gap-4 text-sm" aria-label="Project management">
              <Link to={`/pm/projects/${pid}/progress-report`} className="underline inline-flex min-h-[44px] items-center" data-testid="pm-report-link">Weekly progress report</Link>
              <Link to={`/pm/projects/${pid}/setup`} className="underline inline-flex min-h-[44px] items-center" data-testid="pm-setup-link">Project setup</Link>
              <Link to={`/pm/projects/${pid}/threads`} className="underline inline-flex min-h-[44px] items-center" data-testid="pm-threads-link">Project threads</Link>
            </nav>
          </>
        )}
      </div>
    </AppShell>
  );
}
